import Recommendations, {
  AlgonomyPlacement,
} from '@components/global/Recommendations';
import Container from '@components/global/Container';

import {
  InteractionLocation,
  PromotionEventName,
  trackPromotionEvent,
} from '@utils/gtm/ga4/trackPromotionEvents';
import { determinePageType, buildCreativeSlotName } from '@utils/gtm/ga4/utils';

import { generateProductTrackingData } from '@utils/gtm/ga4';

import styles from './BestSellerRecommendations.module.scss';

type TrackSlideViewCallback = (data?: {
  placement: AlgonomyPlacement;
  products: (ReturnType<typeof generateProductTrackingData> | null)[];
}) => void;

type TrackSlideClickCallback = (data?: {
  placement: AlgonomyPlacement;
  products: (ReturnType<typeof generateProductTrackingData> | null)[];
}) => void;

const processProductData = (
  data:
    | {
        placement: AlgonomyPlacement;
        products: (ReturnType<typeof generateProductTrackingData> | null)[];
      }
    | undefined,
  sliceIndex: number | undefined,
  brandedStoreCoopAttribute: string | null | undefined,
  eventName: (typeof PromotionEventName)[keyof typeof PromotionEventName]
) => {
  const { placement, products } = data || {};

  const productData =
    products?.flatMap((product) => {
      if (!product || !placement) return [];
      const additionalData = placement.items[product?.index];
      return {
        creative_name: `link,${
          additionalData.detailURL
        }?colour=${product?.itemColour.replace(/ /g, '+')}`,
        creative_slot: buildCreativeSlotName({
          sliceIndex,
          interactionLocation: InteractionLocation.BestSellerRecommendations,
          itemIndex: product?.index,
        }),
        promotion_id: additionalData?.trackingimpressionDimension5 || '',
        promotion_name: additionalData?.displayName || '',
        isVideo: false,
      };
    }) ?? [];

  productData.forEach((product) => {
    trackPromotionEvent({
      eventName,
      page: {
        path: window.location.pathname,
        page_type: determinePageType(),
      },
      interaction: {
        coop_brand: brandedStoreCoopAttribute || null,
        interaction_location: InteractionLocation.BestSellerRecommendations,
      },
      ecommerce: {
        ...product,
      },
    });
  });
};

const BestSellerRecommendations = ({
  featuredBestSellersRecommendations,
  brandedStoreCoopAttribute,
  sliceIndex,
}: {
  featuredBestSellersRecommendations?: AlgonomyPlacement[];
  brandedStoreCoopAttribute?: string | null;
  sliceIndex?: number;
}) => {
  if (!featuredBestSellersRecommendations) return null;

  const clickHandler: TrackSlideClickCallback = (data) =>
    processProductData(
      data,
      sliceIndex,
      brandedStoreCoopAttribute,
      PromotionEventName.selectPromotion
    );

  const viewHandler: TrackSlideViewCallback = (data) =>
    processProductData(
      data,
      sliceIndex,
      brandedStoreCoopAttribute,
      PromotionEventName.viewPromotion
    );

  return (
    <Container>
      <div className={styles.bestSellerRecommendations}>
        <Recommendations
          placement={featuredBestSellersRecommendations?.[0]}
          isPlp
          key={featuredBestSellersRecommendations?.[0]?.name}
          trackSlideClickCallback={clickHandler}
          trackSlideViewCallback={viewHandler}
        />
      </div>
    </Container>
  );
};

export default BestSellerRecommendations;
